<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading && !restaurantMeals.length" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-storefront-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <ui-table
      v-else
      variant="report"
      :columns="columns"
      :rows="rows"
      :row-key="(row) => row.key"
      :sort="sort"
      :totals="totals"
      :total-label="$t('total')"
      @sort="sortBy"
    >
      <template #cell-count="{ row }"><b>{{ row.count }}</b></template>

      <template #cell-share="{ row }">
        <span class="rd-share">
          <span class="rd-mini"><i :style="{ width: `${row.relative}%` }"></i></span>
          <span class="rd-muted rd-num">{{ row.share }}%</span>
        </span>
      </template>
    </ui-table>
  </div>
</template>

<script>
import ReportTabGroupMealsTabRestaurants from "@/components/report/group-meals/ReportTabGroupMealsTabRestaurants.vue";
import { copyText, rowsToTsv } from "../../copyTable";
import hasTableSort from "../../hasTableSort";
import ReportState from "../../ReportState.vue";
import ReportTools from "../../ReportTools.vue";

/**
 * Restorani (jela iz grupa) na računaru: koliko je obroka i narudžbi po restoranu koji hrani firme, sa udjelom u
 * ukupnom. Nasleđuje učitavanje, štampu i Excel iz starog taba; ako odgovor ima i svoj red "UKUPNO", on je zbir
 * u podnožju, a ne restoran.
 */
export default {
  name: "ReportDesktopGroupRestaurants",
  extends: ReportTabGroupMealsTabRestaurants,
  mixins: [hasTableSort],
  components: { ReportState, ReportTools },
  data: () => ({
    sort: { key: "count", dir: -1 },
  }),
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "#", label: "#", index: true },
        { key: "name", label: this.$t("restaurant"), name: true, sortable: true },
        { key: "count", label: this.$t("meal_num"), align: "end", num: true, sortable: true },
        { key: "orders_count", label: this.$t("number_of_orders"), align: "end", num: true, zero: true, sortable: true },
        { key: "share", label: this.$t("report_desktop.share"), cls: "rd-col-share" },
      ];
    },
    isTotalRow() {
      const total = String(this.$t("totalU")).toLowerCase();
      return (row) => ["ukupno", total].includes(String(row.name || "").trim().toLowerCase());
    },
    items() {
      return this.translatedRestaurantMeals.filter((row) => !this.isTotalRow(row));
    },
    rows() {
      const found = this.filters.search
        ? this.customSearch(this.items, [{ value: "name" }], this.filters.search)
        : this.items;
      const grand = found.reduce((sum, row) => sum + (row.count || 0), 0);
      const max = Math.max(1, ...found.map((row) => row.count || 0));
      return this.sortRows(
        found.map((row, index) => ({
          ...row,
          key: `${row.name}-${index}`,
          count: row.count || 0,
          orders_count: row.orders_count || 0,
          share: grand ? Math.round(((row.count || 0) / grand) * 100) : 0,
          relative: Math.round(((row.count || 0) / max) * 100),
        }))
      );
    },
    totals() {
      return {
        count: this.rows.reduce((sum, row) => sum + row.count, 0),
        orders_count: this.rows.reduce((sum, row) => sum + row.orders_count, 0),
      };
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("no_data_available");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "meals", label: this.$t("report_desktop.total_meals"), value: this.totals.count, tone: "inverse" },
        { key: "orders", label: this.$t("number_of_orders"), value: this.totals.orders_count },
        { key: "restaurants", label: this.$t("restaurants"), value: this.rows.length },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    async copyTable() {
      const headers = [this.$t("restaurant"), this.$t("meal_num"), this.$t("number_of_orders")];
      const rows = this.rows.map((row) => [row.name, row.count, row.orders_count]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
